import {
  EConditionLogicalOperator,
  EConditionOperator,
  TApprovalRuleConditions,
  TApprovalRules,
  TCustomFieldCondition,
  WorkflowPhase,
} from '@modules/users/types/organizations.types';
import { TCustomFieldValue } from '@modules/items';
import { shallowEqual } from '@shared/helpers';

const getPhaseOrder = (phase: WorkflowPhase): number => {
  switch (phase) {
    case WorkflowPhase.AUTHORING:
      return 0;
    case WorkflowPhase.REVIEWING:
      return 1;
    case WorkflowPhase.APPROVING:
      return 2;
  }
};

const buildExclusiveUsersMessage = (
  phaseA: WorkflowPhase,
  phaseB: WorkflowPhase,
  phaseLabels: Record<WorkflowPhase, string>
): string => {
  const [firstPhase, secondPhase] =
    getPhaseOrder(phaseA) <= getPhaseOrder(phaseB) ? [phaseA, phaseB] : [phaseB, phaseA];
  return `A user can't be assigned as both ${phaseLabels[firstPhase]} and ${phaseLabels[secondPhase]}.`;
};

// Condition evaluation logic is mirrored in the backend (approvalRuleUtils.ts)
// but intentionally NOT placed in backendFrontend/ - the BE throws ApiException
// while the FE returns string[], and the two use different type systems.

// ── Condition Evaluation ────────────────────────

const evaluateCustomFieldCondition = (
  condition: TCustomFieldCondition,
  customFieldValues: TCustomFieldValue[]
): boolean => {
  const cfValue = customFieldValues.find((v) => v.customField === condition.fieldId);
  if (!cfValue) return false;

  switch (condition.operator) {
    case EConditionOperator.EQUALS:
      return shallowEqual(cfValue.value, condition.value);
    default:
      console.warn(`Unsupported condition operator: ${condition.operator}`);
      return false;
  }
};

export const evaluateConditions = (
  conditions: TApprovalRuleConditions | undefined,
  customFieldValues: TCustomFieldValue[]
): boolean => {
  if (!conditions) return true;

  const results: boolean[] = [
    ...(conditions.customFieldConditions ?? []).map((c) => evaluateCustomFieldCondition(c, customFieldValues)),
    // future: ...(conditions.folderConditions ?? []).map(...)
  ];

  if (results.length === 0) return true;

  switch (conditions.operator) {
    case EConditionLogicalOperator.AND:
      return results.every(Boolean);
    // future: case EConditionLogicalOperator.OR: return results.some(Boolean);
    default:
      console.warn(`Unsupported logical operator: ${conditions.operator}`);
      return false;
  }
};

// ── Rule Evaluation ─────────────────────────────

/**
 * Validate a user selection set against the organisation's rules.
 * @returns list of human‑readable error messages (empty ⟺ valid)
 */
const evalApprovalRules = (
  rules: TApprovalRules | undefined,
  selections: Record<WorkflowPhase, string[]>,
  customFieldValues: TCustomFieldValue[] = []
): string[] => {
  if (!rules) return [];

  const PHASE_LABEL: Record<WorkflowPhase, string> = {
    [WorkflowPhase.AUTHORING]: 'author',
    [WorkflowPhase.REVIEWING]: 'reviewer',
    [WorkflowPhase.APPROVING]: 'approver',
  };

  const msgs: string[] = [];

  // Minimum‑Users
  rules.minimumUsers?.forEach(({ phase, minUsers, conditions }) => {
    if (!evaluateConditions(conditions, customFieldValues)) return;
    const list = selections[phase];
    if (list.length < minUsers)
      msgs.push(`Select at least ${minUsers} ${PHASE_LABEL[phase]}${minUsers > 1 ? 's' : ''}.`);
  });

  // Unique‑Users
  rules.uniqueUsers?.forEach(({ phaseA, phaseB, exclusive, conditions }) => {
    if (!evaluateConditions(conditions, customFieldValues)) return;
    const listA = selections[phaseA];
    const listBSet = new Set(selections[phaseB]);

    if (exclusive) {
      if (listA.some((id) => listBSet.has(id))) msgs.push(buildExclusiveUsersMessage(phaseA, phaseB, PHASE_LABEL));
      return;
    }

    if (listA.length && listA.every((id) => listBSet.has(id)))
      msgs.push(`Add one ${PHASE_LABEL[phaseA]} who is not also the ${PHASE_LABEL[phaseB]}.`);
  });

  return msgs;
};

export default evalApprovalRules;
